import { useCallback, useMemo, useRef, useEffect } from 'react';
import { useCustomizationStore } from '../store/customizationStore';
import type { CustomizationOptions, SurfaceShape, Material, ShapeConstraints, PremiumService } from '../types/customization';
import { nanoid } from 'nanoid';
import { produce } from 'immer';
import { calculateArea, calculateMaterialCost, calculateVolume, calculateShippingWeight } from '../utils/units';
import { debounce } from '../lib/utils';

// Updated shape constraints based on standard 1600x3200mm slab
// All dimensions are in mm and account for industry standards and material optimization
const shapeConstraints: ShapeConstraints = {
  rectangular: {
    width: { min: 600, max: 1600 }, // Standard rectangular constraints
    length: { min: 1000, max: 3200 }
  },
  round: {
    width: { min: 800, max: 1600 }, // Perfect circle, diameter constraints
    length: { min: 800, max: 1600 }  // Must match width for perfect circle
  },
  oval: {
    width: { min: 800, max: 1400 }, // Classic oval proportions
    length: { min: 1600, max: 2400 } // Typically 1.6-1.8x the width
  },
  racetrack: {
    width: { min: 800, max: 1200 }, // Standard racetrack proportions
    length: { min: 1800, max: 2800 } // Typically 2-2.5x the width
  },
  'danish-oval': {
    width: { min: 900, max: 1400 }, // Danish modern proportions
    length: { min: 1800, max: 2600 } // Golden ratio approximately 1:1.618
  },
  kidney: {
    width: { min: 900, max: 1400 }, // Ergonomic kidney shape
    length: { min: 1800, max: 2400 } // Optimal for conversation groups
  },
  'super-elliptical': {
    width: { min: 900, max: 1400 }, // Piet Hein's superellipse proportions
    length: { min: 1800, max: 2800 } // Mathematically optimized ratio
  },
  'boat-shaped': {
    width: { min: 1000, max: 1400 }, // Tapered boat shape
    length: { min: 2200, max: 3000 } // Conference-style proportions
  },
  'conference': {
    width: { min: 1200, max: 1600 }, // Professional conference sizing
    length: { min: 2400, max: 3200 } // Accommodates 8-12 people
  }
};

const VAT_RATE = 0.21; // 21% VAT

interface PriceCalculation {
  basePrice: number;
  servicesTotal: number;
  vatAmount: number;
  total: number;
  pricePerM2: number;
  shippingWeight: number;
  volume: number;
}

interface UseCustomizationOptions {
  onDimensionChange?: (field: string, value: number) => void;
  onError?: (error: Error) => void;
  validateOnChange?: boolean;
  calculateShipping?: boolean;
}

export function useCustomization(options: UseCustomizationOptions = {}) {
  const { 
    customizationOptions,
    selectedMaterial,
    setCustomizationOptions
  } = useCustomizationStore();

  const prevOptionsRef = useRef(customizationOptions);
  const changeTimeoutRef = useRef<NodeJS.Timeout>();
  const uniqueIdRef = useRef(nanoid());
  const lastUpdateRef = useRef<number>(0);

  // Memoize constraints based on current shape
  const currentConstraints = useMemo(() => {
    if (!customizationOptions?.shape) return shapeConstraints.rectangular;
    return shapeConstraints[customizationOptions.shape];
  }, [customizationOptions?.shape]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (changeTimeoutRef.current) {
        clearTimeout(changeTimeoutRef.current);
      }
    };
  }, []);

  // Track changes and notify
  useEffect(() => {
    if (options.onDimensionChange && customizationOptions !== prevOptionsRef.current) {
      const changes = findChanges(prevOptionsRef.current, customizationOptions);
      changes.forEach(([field, value]) => {
        options.onDimensionChange(field, value);
      });
    }
    prevOptionsRef.current = customizationOptions;
  }, [customizationOptions, options.onDimensionChange]);

  const validateDimensions = useCallback((
    width: number,
    length: number,
    shape: SurfaceShape
  ): boolean => {
    try {
      const constraints = shapeConstraints[shape];
      const isValid = width >= constraints.width.min &&
                     width <= constraints.width.max &&
                     length >= constraints.length.min &&
                     length <= constraints.length.max;

      if (!isValid && options.onError) {
        options.onError(new Error(`Invalid dimensions for ${shape} shape`));
      }

      return isValid;
    } catch (error) {
      if (options.onError) {
        options.onError(error instanceof Error ? error : new Error('Validation error'));
      }
      return false;
    }
  }, [options.onError]);

  // Throttle dimension updates to prevent lag
  const updateDimensions = useCallback((
    field: 'width' | 'length' | 'thickness',
    value: number
  ) => {
    if (!customizationOptions) return;

    const now = Date.now();
    if (now - lastUpdateRef.current < 16) return; // Skip updates faster than 60fps
    lastUpdateRef.current = now;

    const shape = customizationOptions.shape || 'rectangular';
    const constraints = shapeConstraints[shape];

    // Use Immer for immutable state updates
    const nextOptions = produce(customizationOptions, draft => {
      // For round surfaces, keep width and length equal
      if (shape === 'round' && (field === 'width' || field === 'length')) {
        const newValue = Math.max(
          constraints.width.min,
          Math.min(constraints.width.max, value)
        );
        draft.width = newValue;
        draft.length = newValue;
        return;
      }

      // For other shapes, apply individual constraints
      if (field === 'width' || field === 'length') {
        const fieldConstraints = constraints[field];
        draft[field] = Math.max(
          fieldConstraints.min,
          Math.min(fieldConstraints.max, value)
        );
      } else if (field === 'thickness') {
        draft.thickness = value || 12;
      }
    });

    // Validate if enabled
    if (options.validateOnChange) {
      const isValid = validateDimensions(
        nextOptions.width,
        nextOptions.length,
        nextOptions.shape
      );
      if (!isValid) return;
    }

    setCustomizationOptions(nextOptions);
  }, [customizationOptions, setCustomizationOptions, validateDimensions, options.validateOnChange]);

  // Debounced version for smoother slider interaction
  const handleDimensionChange = useMemo(() => 
    debounce(updateDimensions, 16), // ~60fps
  [updateDimensions]);

  // Find changes between old and new options
  const findChanges = (prev: CustomizationOptions | null, next: CustomizationOptions | null) => {
    const changes: [string, number][] = [];
    if (!prev || !next) return changes;

    ['width', 'length', 'thickness'].forEach(field => {
      if (prev[field] !== next[field]) {
        changes.push([field, next[field]]);
      }
    });

    return changes;
  };

  return {
    currentConstraints,
    validateDimensions,
    handleDimensionChange,
    VAT_RATE,
    id: uniqueIdRef.current
  };
}